import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FormFieldInput } from "@/components/FormFieldInput";
import { WEDDING_FIELDS, emptyValues, buildAnswers } from "@/lib/forms";
import { Check, Heart } from "lucide-react";
import { toast } from "sonner";

const PRICING = [
  { tier: "2 Tier Cake", price: "$450 – $500 +" },
  { tier: "3 Tier Cake", price: "$650 – $700 +" },
  { tier: "4–5 Tier Cake", price: "$850 +" },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-bold text-sugar-ink">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default function Wedding() {
  const navigate = useNavigate();
  const [f, setF] = useState(emptyValues(WEDDING_FIELDS));
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    const missing = WEDDING_FIELDS.find((x) => x.required && !f[x.key]);
    if (missing) { toast.error(`Please fill in: ${missing.label}`); return; }
    setSending(true);
    try {
      await api.post("/wedding-inquiry", { ...f, answers: buildAnswers(WEDDING_FIELDS, f) });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
    setSending(false);
  };

  if (done) {
    return (
      <div className="App bg-sugar-bg">
        <Navbar />
        <section className="px-5 py-16 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-sugar-pink text-white">
            <Check size={30} />
          </motion.div>
          <h1 className="mt-5 font-display text-3xl font-bold text-sugar-ink">Thank you for inquiring!</h1>
          <p className="mx-auto mt-3 max-w-sm text-sugar-ink2">I'll be in touch shortly with a breakdown of pricing for your special day. — Sarah</p>
          <Button data-testid="wedding-home-btn" onClick={() => navigate("/")} className="mt-7 rounded-full bg-sugar-ink px-8 py-6 font-bold text-sugar-bg hover:bg-sugar-pinkhover">Back to home</Button>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App bg-sugar-bg">
      <Navbar />

      <section className="sugar-hero-bg px-5 pb-8 pt-8">
        <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.28em] text-sugar-ink2"><Heart size={13} className="text-sugar-rose" fill="#C08A8A" /> Wedding Cakes</span>
        <h1 className="mt-3 font-display text-[2.4rem] font-semibold leading-tight text-sugar-ink">Your dream <span className="italic font-normal text-sugar-rose">wedding</span> cake</h1>
        <p className="mt-4 text-[15px] leading-relaxed text-sugar-ink2">
          Thank you for choosing My Sugar Obsession for your beautiful Wedding Cake. I'm Sarah, the baker who takes care of everything on your wedding day. Fill in the form below so I understand what you're looking for, and I'll get back to you with pricing right away.
        </p>
        <p className="mt-3 text-[15px] leading-relaxed text-sugar-ink2">
          My prices include everything — your cake, flowers, toppers, multiple flavours & delivery. You'll receive a contract with all the details so you feel confident your cake is taken care of.
        </p>
      </section>

      {/* pricing cards */}
      <section className="px-5 py-6">
        <h2 className="font-display text-2xl font-bold text-sugar-ink">Pricing estimation</h2>
        <div className="mt-4 grid gap-3">
          {PRICING.map((p) => (
            <div key={p.tier} data-testid={`wedding-price-${p.tier}`} className="rounded-2xl border border-sugar-border bg-white p-5">
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-lg font-bold text-sugar-ink">{p.tier}</h3>
                <span className="font-display text-xl font-extrabold text-sugar-pink">{p.price}</span>
              </div>
              <ul className="mt-2 space-y-1 text-sm text-sugar-ink2">
                <li>• Choose any flavour (1 per tier)</li>
                <li>• Includes flowers & topper</li>
                <li>• Stress-free delivery & setup</li>
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* form */}
      <section className="px-5 pb-10">
        <div className="rounded-3xl border border-sugar-border bg-white p-6 sugar-card-shadow">
          <h2 className="font-display text-2xl font-bold text-sugar-ink">Inquiry form</h2>
          <div className="mt-5 space-y-4">
            {WEDDING_FIELDS.map((field) => (
              <Field key={field.key} label={`${field.label}${field.required ? " *" : ""}`}>
                <FormFieldInput field={field} value={f[field.key]} onChange={(v) => set(field.key, v)} />
              </Field>
            ))}
            <Button data-testid="wedding-submit" disabled={sending} onClick={submit} className="w-full rounded-full bg-sugar-ink py-6 text-sm font-bold uppercase tracking-[0.18em] text-sugar-bg hover:bg-sugar-pinkhover">
              {sending ? "Sending…" : "Submit inquiry"}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
